"use client";

import { KeyRound, ShieldCheck } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { withReturnTo } from "@/lib/auth/return-to";
import { LoginForm } from "@/components/auth/login-form";
import type { DevLoginPanelData } from "@/lib/auth/dev-login-types";

interface LoginCardProps {
  productName: string;
  returnTo: string;
  /**
   * Development quick-login personas, or `null`/absent in every other case.
   *
   * The decision is made **on the server** (`app/login/page.tsx` →
   * `devLoginGate()`), so in a production deployment these props simply do not
   * exist and the panel has nothing to render — there is no client-side flag to
   * flip and no credential in the payload to find.
   */
  devLogin?: DevLoginPanelData | null;
}

/**
 * Hybrid login card: SSO button (existing OIDC BFF flow) above the local
 * credential form. The card is the pre-auth surface, so it uses the neutral
 * product name — tenant branding only exists after a tenant is known.
 */
export function LoginCard({ productName, returnTo }: LoginCardProps) {
  const { t } = useTranslation();

  return (
    <div className="mx-auto w-full max-w-md rounded-lg border border-border bg-card p-8 text-card-foreground shadow-sm">
      <div className="mb-6 space-y-2 text-center">
        <span className="mx-auto flex size-12 items-center justify-center rounded-md bg-primary/10 text-primary">
          <ShieldCheck className="size-6" aria-hidden />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{productName}</h1>
        <p className="text-sm text-muted-foreground">{t("auth.local.subtitle")}</p>
      </div>

      <a
        // The deep link travels with the SSO hop too, not only with the local
        // form below: `/api/auth/login` seals it into the OIDC state cookie.
        href={withReturnTo("/api/auth/login", returnTo)}
        data-testid="sso-login"
        className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <KeyRound className="size-4" aria-hidden />
        {t("auth.local.ssoButton")}
      </a>

      <div className="my-6 flex items-center gap-3" role="separator" aria-hidden>
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {t("auth.local.orDivider")}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <LoginForm returnTo={returnTo} />

    </div>
  );
}
